import { DollarSign, Users, Link2, TrendingUp, AlertCircle, ArrowRight, Zap, Waves } from 'lucide-react';
import { useDashboardStats, useAffiliateProfile } from '../hooks/useAffiliate';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading, error: statsError } = useDashboardStats();
  const { data: profile, isLoading: profileLoading } = useAffiliateProfile();

  const isLoading = statsLoading || profileLoading;

  // Profile status banner
  const showStatusBanner = profile && profile.status !== 'APPROVED';

  const statCards = stats
    ? [
      {
        name: 'Total Earnings',
        value: `KES ${stats.overview.totalEarnings.toLocaleString()}`,
        icon: DollarSign,
        change: stats.earnings.pending > 0 ? `KES ${stats.earnings.pending.toLocaleString()} pending` : 'No pending payouts',
        changeType: 'neutral',
        gradient: 'from-emerald-500/10 to-transparent',
      },
      {
        name: 'Active Links',
        value: stats.topPerformingLinks.length.toString(),
        icon: Link2,
        change: 'Manage your links',
        changeType: 'neutral',
        gradient: 'from-blue-500/10 to-transparent',
      },
      {
        name: 'Total Clicks',
        value: stats.overview.totalClicks.toLocaleString(),
        icon: Users,
        change: `${stats.overview.totalSales} total sales`,
        changeType: 'positive',
        gradient: 'from-purple-500/10 to-transparent',
      },
      {
        name: 'Conversion Rate',
        value: `${stats.overview.conversionRate.toFixed(2)}%`,
        icon: TrendingUp,
        change: stats.overview.totalSales > 0 ? 'Generating sales' : 'No sales yet',
        changeType: stats.overview.totalSales > 0 ? 'positive' : 'neutral',
        gradient: 'from-amber-500/10 to-transparent',
      },
    ]
    : [];

  if (statsError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="bg-white dark:bg-zinc-900 border border-red-100 dark:border-red-900/30 rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 uppercase tracking-tight">Sync Delayed</h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-2 leading-relaxed">
            We're having trouble reaching the network. Please refresh to restore your analytics.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-16 animate-in fade-in duration-700 space-y-12">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
          Dashboard
        </h1>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <p className="text-zinc-500 dark:text-zinc-400 font-medium tracking-tight">
            Welcome back, {profile?.displayName || 'Partner'}
          </p>
          <div className="hidden sm:block h-1 w-1 rounded-full bg-zinc-300 dark:bg-zinc-700"></div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-600/80">Affiliate</p>
        </div>
      </div>

      {/* Status Banner */}
      {showStatusBanner && (
        <div className="relative overflow-hidden rounded-2xl p-5 md:p-6 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 shadow-sm">
          <div className="absolute top-0 left-0 w-1 h-full bg-amber-500"></div>
          <div className="flex items-start md:items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-amber-600 uppercase tracking-[0.2em] mb-0.5">Account Status</p>
              <p className="font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                {profile.status === 'PENDING' && 'Under Review'}
                {profile.status === 'REJECTED' && 'Not Approved'}
                {profile.status === 'SUSPENDED' && 'Suspended'}
                {profile.status === 'INACTIVE' && 'Inactive'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl p-6 md:p-8 aspect-[4/3] animate-pulse"></div>
          ))
        ) : (
          statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.name} className="group relative bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl p-6 md:p-8 hover:shadow-xl hover:shadow-zinc-500/5 transition-all">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl`}></div>
                <div className="relative z-10 space-y-4 md:space-y-6">
                  <div className="w-10 h-10 rounded-xl bg-zinc-50 dark:bg-zinc-800 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em]">{stat.name}</label>
                    <p className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-100 mt-1 tracking-tight">
                      {stat.value}
                    </p>
                    <p className="mt-1 flex items-center gap-1.5 font-medium text-xs text-zinc-500">
                      {stat.change}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Top Links & Growth Strategy */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 pt-4">
        <div className="lg:col-span-8 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden shadow-sm">
          <div className="p-6 md:p-8 border-b border-zinc-50 dark:border-zinc-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">Top Links</h2>
              <p className="text-xs text-zinc-400 font-medium mt-1">Your best performing product links.</p>
            </div>
            <Link
              to="/links"
              className="group text-[10px] font-bold text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 uppercase tracking-[0.2em] flex items-center gap-2 transition-colors"
            >
              View All
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="p-4">
            {stats && stats.topPerformingLinks.length > 0 ? (
              <div className="space-y-1">
                {stats.topPerformingLinks.slice(0, 5).map((link) => (
                  <div key={link.linkId} className="group flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors gap-4">
                    <div className="space-y-1">
                      <p className="font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">{link.productTitle}</p>
                      <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest">ID: {link.code}</p>
                    </div>
                    <div className="flex items-center gap-8 sm:gap-12">
                      <div className="text-left sm:text-right">
                        <p className="text-xs font-bold text-zinc-900 dark:text-zinc-100">{link.clicks}</p>
                        <p className="text-[9px] text-zinc-400 uppercase tracking-widest">Clicks</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-emerald-600">KES {link.commissions.toLocaleString()}</p>
                        <p className="text-[9px] text-zinc-400 uppercase tracking-widest">Profit</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-16 md:py-20 text-center space-y-4">
                <div className="w-12 h-12 rounded-full bg-zinc-50 dark:bg-zinc-800 flex items-center justify-center mx-auto">
                  <Waves className="w-6 h-6 text-zinc-300" />
                </div>
                <p className="text-xs text-zinc-400 font-bold uppercase tracking-[0.2em]">No data yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Get Started */}
        <div className="lg:col-span-4 space-y-8">
          <div className="relative overflow-hidden bg-emerald-950 rounded-3xl p-6 md:p-8 text-white group">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-400/60 mb-6 border-b border-white/10 pb-4">Get Started</p>

            <div className="space-y-6 md:space-y-8 relative z-10">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Link2 className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-sm tracking-tight text-white">Create Links</h3>
                  <p className="text-xs text-emerald-100/60 mt-1 leading-relaxed">Choose products and create your unique tracking links.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-sm tracking-tight text-white">Share & Earn</h3>
                  <p className="text-xs text-emerald-100/60 mt-1 leading-relaxed">Share links on social media and earn for every sale.</p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/links"
                  className="flex items-center justify-center gap-2 w-full py-4 bg-white text-emerald-950 rounded-2xl text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-emerald-50 active:scale-[0.98] transition-all"
                >
                  Start Now
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
